import React from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import { isString } from 'lodash';
import cn from 'classnames';
import ErrorMessage from 'shared/components/ErrorMessage';
import styles from './Autocomplete.module.scss';

class Autocomplete extends React.Component {
  static propTypes = {
    defaultValue: PropTypes.string,
    externalValue: PropTypes.string,
    fetchSuggestions: PropTypes.func.isRequired,
    renderSuggestion: PropTypes.func,
    renderSuggestionKey: PropTypes.string,
    onChange: PropTypes.func,
    onSelect: PropTypes.func,
    onError: PropTypes.func,
    onLoading: PropTypes.func,
    onSuggestionsContainerDisplay: PropTypes.func,
    onRemoveLastSelected: PropTypes.func,
    onEnterKeyPress: PropTypes.func,
    onSuggestionsClose: PropTypes.func,
    delay: PropTypes.number,
    id: PropTypes.string,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    createUnknown: PropTypes.func,
    createNothingFound: PropTypes.func,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    iconClassName: PropTypes.string,
    error: PropTypes.string,
    theme: PropTypes.shape({
      container: PropTypes.string,
      containerOpen: PropTypes.string,
      input: PropTypes.string,
      inputOpen: PropTypes.string,
      inputFocused: PropTypes.string,
      suggestionsContainer: PropTypes.string,
      suggestionsContainerOpen: PropTypes.string,
      suggestionsList: PropTypes.string,
      suggestion: PropTypes.string,
      suggestionFirst: PropTypes.string,
      suggestionHighlighted: PropTypes.string,
      sectionContainer: PropTypes.string,
      sectionContainerFirst: PropTypes.string,
      sectionTitle: PropTypes.string
    })
  };

  static defaultProps = {
    defaultValue: '',
    externalValue: null,
    renderSuggestion: null,
    renderSuggestionKey: 'name',
    onChange() {},
    onSelect(suggestion) {
      return suggestion.name;
    },
    onError() {},
    onLoading() {},
    onSuggestionsContainerDisplay() {},
    onSuggestionsClose: null,
    onRemoveLastSelected() {},
    onEnterKeyPress: null,
    delay: 300,
    id: 'autocomplete',
    label: null,
    placeholder: 'Type to search',
    createUnknown: null,
    createNothingFound: null,
    disabled: false,
    loading: false,
    iconClassName: null,
    error: null,
    theme: {}
  };

  constructor(props) {
    super(props);

    this.state = {
      value: props.defaultValue,
      suggestions: [],
      loading: false
    };

    this.lastRequestId = null;
  }

  // TODO: refactor to fn component !!!!!
  static getDerivedStateFromProps(props, state) {
    if (props.externalValue === '' && state.value) {
      return {
        ...state,
        value: ''
      };
    }

    return state;
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.suggestions.length !== this.state.suggestions.length) {
      this.props.onSuggestionsContainerDisplay(this.state.suggestions.length > 0);
    }
  }

  onChange = (event, { newValue }) => {
    this.setState({
      value: newValue
    });
    this.props.onChange(newValue);
  };

  onKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      let value = '';
      if (this.props.onEnterKeyPress) {
        value = this.props.onEnterKeyPress(event.target.value, this.state.suggestions);
      } else if (this.state.suggestions.length > 0) {
        value = this.props.onSelect(this.state.suggestions[0]);
      }
      this.setState({ value: value || '', suggestions: [] });
    }
  };

  onKeyDown = (key) => {
    if (key.keyCode === 8 && !this.state.value) {
      this.props.onRemoveLastSelected();
    }
  };

  onSuggestionsFetchRequested = ({ value }) => {
    // Cancel the previous request
    if (this.lastRequestId !== null) {
      clearTimeout(this.lastRequestId);
    }

    this.setState({ loading: true });
    this.props.onLoading(true);

    this.lastRequestId = setTimeout(async () => {
      try {
        let suggestions = await this.props.fetchSuggestions(value);

        if ((suggestions || []).length === 0) {
          if (this.props.createNothingFound !== null) {
            suggestions = [this.props.createNothingFound()];
          } else if (this.props.createUnknown !== null) {
            const unknown = this.props.createUnknown(value);
            if (Array.isArray(unknown)) {
              suggestions = [...unknown];
            } else {
              suggestions = [unknown];
            }
          }
        }

        this.setState({ loading: false, suggestions });
        this.props.onLoading(false);
      } catch (e) {
        this.setState({ loading: false });
        this.props.onLoading(false);
        this.props.onError(e);
      }
    }, this.props.delay);
  };

  onSuggestionsClearRequested = () => {
    if (this.props.onSuggestionsClose === null) {
      this.setState({ suggestions: [] });
    } else {
      setTimeout(() => {
        const value = this.props.onSuggestionsClose(this.state.value, this.state.suggestions);
        this.setState({
          value,
          suggestions: []
        });
      });
    }
  };

  getSuggestionValue = (suggestion) => {
    const value = this.props.onSelect(suggestion);
    if (!(value === null || value === undefined)) {
      return value;
    }

    return suggestion.value || suggestion[this.props.renderSuggestionKey];
  };

  renderSuggestion = (suggestion) => {
    if (this.props.renderSuggestion !== null) {
      return this.props.renderSuggestion(suggestion);
    }

    return (
      <div data-disabled={suggestion.disabled}>
        {suggestion[this.props.renderSuggestionKey]}
      </div>
    );
  };

  render() {
    const { value, suggestions } = this.state;

    const inputProps = {
      id: this.props.id,
      placeholder: this.props.placeholder,
      value,
      onChange: this.onChange,
      onKeyPress: this.onKeyPress,
      onKeyDown: this.onKeyDown,
      disabled: this.props.disabled || this.props.loading,
      'data-hj-allow': true,
    };

    const hasError = !!this.props.error;

    return (
      <>
        {this.props.label && (
          <label htmlFor={this.props.id} className={cn(styles.label, { [styles.error]: hasError, error: hasError })}>{this.props.label}</label>
        )}
        <div className={styles.wrapper}>
          <Autosuggest
            suggestions={suggestions}
            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
            getSuggestionValue={this.getSuggestionValue}
            renderSuggestion={this.renderSuggestion}
            inputProps={inputProps}
            theme={{
              container: cn(styles.container, this.props.theme.container),
              suggestionsContainerOpen: cn(styles.suggestionsContainerOpen, this.props.theme.suggestionsContainerOpen),
              suggestionsList: cn(styles.suggestionsList, this.props.theme.suggestionsList),
              suggestionHighlighted: cn(styles.suggestionHighlighted, this.props.theme.suggestionHighlighted),
              suggestion: cn(styles.suggestion, this.props.theme.suggestion),
              input: cn(styles.input, { [styles.inputError]: hasError }, this.props.theme.input),
              inputFocused: cn(styles.inputFocused, this.props.theme.inputFocused),
              // ...this.props.theme
            }}
            // renderSuggestionsContainer={options => (
            //   <div {...options.containerProps}>
            //     {options.children}
            //   </div>
            // )}
          />
          {this.props.iconClassName && (
            <i className={cn(styles.icon, this.props.iconClassName)} />
          )}
        </div>
        {isString(this.props.error) && <ErrorMessage>{this.props.error}</ErrorMessage>}
      </>
    );
  }
}

export default Autocomplete;
